import { useState } from 'react';
import { 
  Target, FileText, Loader2, Download, Copy, Check, 
  ChevronDown, ChevronUp, Sparkles, ClipboardList, Lightbulb, ListChecks
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IEPGoalGenerator = ({ storyId, studentName, scenario, isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [iepData, setIepData] = useState(null);
  const [includeObjectives, setIncludeObjectives] = useState(false);
  const [customContext, setCustomContext] = useState('');
  const [copiedField, setCopiedField] = useState(null);
  const [objectivesExpanded, setObjectivesExpanded] = useState(true);
  const [generatingWorksheet, setGeneratingWorksheet] = useState(false);
  const [generatingPBL, setGeneratingPBL] = useState(false);
  const [generatingTaskAnalysis, setGeneratingTaskAnalysis] = useState(false);
  const [worksheetData, setWorksheetData] = useState(null);
  const [pblData, setPblData] = useState(null);
  const [taskAnalysisData, setTaskAnalysisData] = useState(null);

  const generateIEPGoal = async () => {
    setLoading(true);
    setIepData(null);
    
    try {
      const response = await axios.post(
        `${API}/stories/${storyId}/generate-iep-goal`,
        {
          include_objectives: includeObjectives,
          custom_context: customContext || null
        },
        { withCredentials: true }
      );
      
      setIepData(response.data);
      toast.success('IEP goal generated!');
    } catch (error) {
      console.error('Error generating IEP goal:', error);
      toast.error('Failed to generate IEP goal');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  // Generate Progress Monitoring Worksheet
  const generateWorksheet = async () => {
    if (!iepData?.iep_goal?.annual_goal) return;
    
    setGeneratingWorksheet(true);
    try {
      const goal = iepData.iep_goal.annual_goal;
      const response = await axios.post(
        `${API}/generate-progress-monitoring-worksheet`,
        {
          goal_text: goal.goal_text,
          goal_domain: goal.area_of_need || iepData.scenario,
          grade_level: iepData.grade_level,
          student_name: iepData.student_name,
          measurement_method: goal.measurement_method,
          target_criteria: goal.target_criteria
        },
        { withCredentials: true }
      );
      
      setWorksheetData(response.data);
      toast.success('Worksheet generated!');
    } catch (error) {
      console.error('Error generating worksheet:', error);
      toast.error('Failed to generate worksheet');
    } finally {
      setGeneratingWorksheet(false);
    }
  };

  // Download Progress Monitoring Worksheet as PDF
  const downloadWorksheetPdf = () => {
    if (!worksheetData?.worksheet) return;
    
    const ws = worksheetData.worksheet;
    const worksheetType = worksheetData.worksheet_type || 'general';
    const pdf = new jsPDF('portrait', 'mm', 'letter');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - 2 * margin;
    
    const darkSage = [58, 90, 64];
    
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    
    let yPos = margin;
    
    // Title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.setTextColor(...darkSage);
    pdf.text(ws.worksheet_title || 'Practice Worksheet', pageWidth / 2, yPos + 8, { align: 'center' });
    yPos += 15;
    
    // Student info
    pdf.setFontSize(11);
    pdf.setTextColor(80, 80, 80);
    pdf.text(`Name: ${worksheetData.student_name || '_________________'}`, margin, yPos);
    pdf.text(`Date: _________________`, pageWidth - margin - 50, yPos);
    yPos += 10;
    
    // Goal/Skill summary
    if (ws.goal_summary) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(...darkSage);
      pdf.text('Skill:', margin, yPos);
      yPos += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(60, 60, 60);
      const goalLines = pdf.splitTextToSize(ws.goal_summary, contentWidth);
      pdf.text(goalLines, margin, yPos);
      yPos += goalLines.length * 4 + 5;
    }
    
    // Instructions for student
    if (ws.instructions_for_student) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(...darkSage);
      pdf.text('Instructions:', margin, yPos);
      yPos += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(60, 60, 60);
      const instrLines = pdf.splitTextToSize(ws.instructions_for_student, contentWidth);
      pdf.text(instrLines, margin, yPos);
      yPos += instrLines.length * 4 + 8;
    }
    
    // Draw a separator line
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;
    
    // Handle different worksheet types
    if (worksheetType === 'reading' && ws.reading_passage) {
      // READING WORKSHEET
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(...darkSage);
      pdf.text(ws.reading_passage.title || 'Reading Passage', pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(40, 40, 40);
      const passageText = ws.reading_passage.text || '';
      const passageLines = pdf.splitTextToSize(passageText, contentWidth);
      
      passageLines.forEach((line) => {
        if (yPos > pageHeight - 30) {
          pdf.addPage();
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          yPos = margin;
        }
        pdf.text(line, margin, yPos);
        yPos += 5;
      });
      
      yPos += 10;
      
      // Comprehension Questions
      if (ws.comprehension_questions?.length > 0) {
        if (yPos > pageHeight - 60) {
          pdf.addPage();
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          yPos = margin;
        }
        
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(...darkSage);
        pdf.text('Comprehension Questions', margin, yPos);
        yPos += 8;
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(40, 40, 40);
        
        ws.comprehension_questions.forEach((q, i) => {
          if (yPos > pageHeight - 25) {
            pdf.addPage();
            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, 0, pageWidth, pageHeight, 'F');
            yPos = margin;
          }
          
          const questionText = `${q.question_number || i + 1}. ${q.question_text}`;
          const qLines = pdf.splitTextToSize(questionText, contentWidth - 5);
          qLines.forEach((line) => {
            pdf.text(line, margin, yPos);
            yPos += 5;
          });
          
          pdf.text(`Answer: ${q.answer_space || '________________________________________'}`, margin + 5, yPos);
          yPos += 12;
        });
      }
    } else if (worksheetType === 'math' && ws.problems?.length > 0) {
      // MATH WORKSHEET
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(...darkSage);
      pdf.text('Problems', margin, yPos);
      yPos += 8;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      pdf.setTextColor(40, 40, 40);
      
      ws.problems.forEach((p, i) => {
        if (yPos > pageHeight - 25) {
          pdf.addPage();
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          yPos = margin;
        }
        
        const problemText = `${p.problem_number || i + 1}. ${p.problem_text}`;
        const pLines = pdf.splitTextToSize(problemText, contentWidth - 10);
        pLines.forEach((line) => {
          pdf.text(line, margin, yPos);
          yPos += 6;
        });
        yPos += 8;
      });
    } else if (ws.practice_items?.length > 0) {
      // GENERAL PRACTICE WORKSHEET
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(...darkSage);
      pdf.text('Practice Items', margin, yPos);
      yPos += 8;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      pdf.setTextColor(40, 40, 40);
      
      ws.practice_items.forEach((item, i) => {
        if (yPos > pageHeight - 25) {
          pdf.addPage();
          pdf.setFillColor(255, 255, 255);
          pdf.rect(0, 0, pageWidth, pageHeight, 'F');
          yPos = margin;
        }
        
        const itemText = `${item.item_number || i + 1}. ${item.item_text}`;
        const itemLines = pdf.splitTextToSize(itemText, contentWidth - 10);
        itemLines.forEach((line) => {
          pdf.text(line, margin, yPos);
          yPos += 6;
        });
        yPos += 8;
      });
    } else if (ws.scoring_guide?.scale) {
      // Fallback: Old format - show scoring guide
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(...darkSage);
      pdf.text('Scoring Guide:', margin, yPos);
      yPos += 6;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(60, 60, 60);
      ws.scoring_guide.scale.forEach((item) => {
        pdf.text(`${item.score} = ${item.label}: ${item.description || ''}`, margin + 5, yPos);
        yPos += 4;
      });
    }
    
    // Footer
    pdf.setFontSize(7);
    pdf.setTextColor(150, 150, 150);
    pdf.text('Created with SpEdKit', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    const fileName = worksheetType === 'reading' ? 'Reading_Worksheet' : 
                     worksheetType === 'math' ? 'Math_Worksheet' : 
                     'Practice_Worksheet';
    pdf.save(`${fileName}_${worksheetData.student_name?.replace(/\s+/g, '_') || 'Student'}.pdf`);
    toast.success('Worksheet downloaded!');
  };

  // Generate Project-Based Learning Ideas
  const generatePBL = async () => {
    if (!iepData?.iep_goal?.annual_goal) return;
    
    setGeneratingPBL(true);
    try {
      const goal = iepData.iep_goal.annual_goal;
      const response = await axios.post(
        `${API}/generate-pbl-ideas`,
        {
          goal_text: goal.goal_text,
          goal_domain: goal.area_of_need || iepData.scenario,
          grade_level: iepData.grade_level,
          student_interests: customContext || null,
          subject_area: goal.area_of_need
        },
        { withCredentials: true }
      );
      
      setPblData(response.data);
      toast.success('Project ideas generated!');
    } catch (error) {
      console.error('Error generating PBL ideas:', error);
      toast.error('Failed to generate project ideas');
    } finally {
      setGeneratingPBL(false);
    }
  };

  // Download PBL Ideas as PDF
  const downloadPBLPdf = () => {
    if (!pblData?.pbl_ideas) return;
    
    const pbl = pblData.pbl_ideas;
    const pdf = new jsPDF('portrait', 'mm', 'letter');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - 2 * margin;
    
    const darkSage = [58, 90, 64];
    
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    
    let yPos = margin;
    
    // Title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(20);
    pdf.setTextColor(...darkSage);
    pdf.text('Project-Based Learning Ideas', pageWidth / 2, yPos + 8, { align: 'center' });
    yPos += 15;
    
    // Goal reference
    pdf.setFontSize(10);
    pdf.setTextColor(80, 80, 80);
    pdf.text(`Aligned to Goal: ${pblData.goal_domain}`, margin, yPos);
    pdf.text(`Grade: ${pblData.grade_level}`, pageWidth - margin - 40, yPos);
    yPos += 12;
    
    // Projects
    (pbl.projects || []).forEach((project, index) => {
      if (yPos > pageHeight - 60) {
        pdf.addPage();
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        yPos = margin;
      }
      
      // Project title
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(...darkSage);
      pdf.text(`${index + 1}. ${project.title}`, margin, yPos);
      yPos += 7;
      
      // Description
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(60, 60, 60);
      const descLines = pdf.splitTextToSize(project.description || '', contentWidth);
      pdf.text(descLines, margin, yPos);
      yPos += descLines.length * 4 + 5;
      
      // Duration
      if (project.duration) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.text(`Duration: `, margin, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(project.duration, margin + 20, yPos);
        yPos += 5;
      }
      
      // IEP connection
      if (project.iep_goal_connection) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(...darkSage);
        pdf.text('IEP Goal Connection:', margin, yPos);
        yPos += 4;
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(60, 60, 60);
        const connLines = pdf.splitTextToSize(project.iep_goal_connection, contentWidth - 5);
        pdf.text(connLines, margin + 5, yPos);
        yPos += connLines.length * 4 + 5;
      }
      
      // Materials
      if (project.materials_needed?.length > 0) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.text('Materials:', margin, yPos);
        yPos += 4;
        pdf.setFont('helvetica', 'normal');
        project.materials_needed.slice(0, 5).forEach((mat) => {
          pdf.text(`• ${mat}`, margin + 5, yPos);
          yPos += 4;
        });
        yPos += 3;
      }
      
      yPos += 10;
    });
    
    // Teacher tips
    if (pbl.teacher_tips?.length > 0 && yPos < pageHeight - 40) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(...darkSage);
      pdf.text('Teacher Tips:', margin, yPos);
      yPos += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(60, 60, 60);
      pbl.teacher_tips.slice(0, 3).forEach((tip) => {
        const tipLines = pdf.splitTextToSize(`• ${tip}`, contentWidth - 5);
        pdf.text(tipLines, margin, yPos);
        yPos += tipLines.length * 4 + 2;
      });
    }
    
    // Footer
    pdf.setFontSize(7);
    pdf.setTextColor(150, 150, 150);
    pdf.text('Created with SpEdKit', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    pdf.save(`PBL_Ideas_${pblData.goal_domain || 'Projects'}.pdf`);
    toast.success('Project ideas downloaded!');
  };

  // Generate Task Analysis Worksheet (for ADL goals)
  const generateTaskAnalysis = async () => {
    if (!iepData?.iep_goal?.annual_goal) return;
    
    setGeneratingTaskAnalysis(true);
    try {
      const goal = iepData.iep_goal.annual_goal;
      const response = await axios.post(
        `${API}/generate-task-analysis`,
        {
          goal_text: goal.goal_text,
          goal_domain: goal.area_of_need || iepData.scenario,
          grade_level: iepData.grade_level,
          student_name: iepData.student_name
        },
        { withCredentials: true }
      );
      
      setTaskAnalysisData(response.data);
      toast.success('Task analysis generated!');
    } catch (error) {
      console.error('Error generating task analysis:', error);
      toast.error('Failed to generate task analysis');
    } finally {
      setGeneratingTaskAnalysis(false);
    }
  };

  // Download Task Analysis as PDF
  const downloadTaskAnalysisPdf = () => {
    if (!taskAnalysisData?.task_analysis) return;
    
    const ta = taskAnalysisData.task_analysis;
    const pdf = new jsPDF('portrait', 'mm', 'letter');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - 2 * margin;
    
    const darkSage = [58, 90, 64];
    
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    
    let yPos = margin;
    
    // Title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.setTextColor(...darkSage);
    pdf.text('Task Analysis Worksheet', pageWidth / 2, yPos + 8, { align: 'center' });
    yPos += 12;
    
    pdf.setFontSize(14);
    pdf.text(ta.task_title || 'Daily Living Skill', pageWidth / 2, yPos + 5, { align: 'center' });
    yPos += 12;
    
    pdf.setFontSize(10);
    pdf.setTextColor(80, 80, 80);
    pdf.text(`Student: ${taskAnalysisData.student_name || '_________________'}`, margin, yPos);
    pdf.text(`Date: _________________`, pageWidth - margin - 50, yPos);
    yPos += 8;
    
    if (ta.skill_category) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(...darkSage);
      pdf.text(`Category: ${ta.skill_category}`, margin, yPos);
      yPos += 8;
    }
    
    if (ta.materials_needed?.length > 0) {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.text('Materials Needed:', margin, yPos);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(60, 60, 60);
      pdf.text(ta.materials_needed.join(', '), margin + 35, yPos);
      yPos += 8;
    }
    
    // Steps table
    pdf.setFillColor(245, 245, 245);
    pdf.rect(margin, yPos, contentWidth, 8, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(60, 60, 60);
    
    const stepCol = 10;
    const actionCol = 70;
    const promptCol = 50;
    
    pdf.text('#', margin + 2, yPos + 5);
    pdf.text('Action Step', margin + stepCol + 2, yPos + 5);
    pdf.text('Verbal Prompt', margin + stepCol + actionCol + 2, yPos + 5);
    pdf.text('+/VP/GP/PP/FP', margin + stepCol + actionCol + promptCol + 2, yPos + 5);
    
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.5);
    pdf.rect(margin, yPos, contentWidth, 8);
    yPos += 8;
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    
    (ta.steps || []).forEach((step, i) => {
      if (yPos > pageHeight - 30) {
        pdf.addPage();
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        yPos = margin;
      }
      
      const rowHeight = 10;
      pdf.rect(margin, yPos, contentWidth, rowHeight);
      pdf.line(margin + stepCol, yPos, margin + stepCol, yPos + rowHeight);
      pdf.line(margin + stepCol + actionCol, yPos, margin + stepCol + actionCol, yPos + rowHeight);
      pdf.line(margin + stepCol + actionCol + promptCol, yPos, margin + stepCol + actionCol + promptCol, yPos + rowHeight);
      
      pdf.setTextColor(60, 60, 60);
      pdf.text(String(step.step_number || i + 1), margin + 3, yPos + 6);
      
      const actionLines = pdf.splitTextToSize(step.action || '', actionCol - 4);
      pdf.text(actionLines[0] || '', margin + stepCol + 2, yPos + 6);
      
      const promptLines = pdf.splitTextToSize(step.verbal_prompt || '', promptCol - 4);
      pdf.text(promptLines[0] || '', margin + stepCol + actionCol + 2, yPos + 6);
      
      yPos += rowHeight;
    });
    
    yPos += 8;
    
    if (ta.data_collection) {
      if (yPos > pageHeight - 40) {
        pdf.addPage();
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        yPos = margin;
      }
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(...darkSage);
      pdf.text('Data Collection:', margin, yPos);
      yPos += 5;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(60, 60, 60);
      
      if (ta.data_collection.scoring) {
        pdf.text(`Scoring Key: ${ta.data_collection.scoring}`, margin + 5, yPos);
        yPos += 4;
      }
      if (ta.data_collection.mastery_criteria) {
        pdf.text(`Mastery: ${ta.data_collection.mastery_criteria}`, margin + 5, yPos);
      }
    }
    
    pdf.setFontSize(7);
    pdf.setTextColor(150, 150, 150);
    pdf.text('Created with SpEdKit', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    pdf.save(`Task_Analysis_${ta.task_title || 'ADL_Skill'}.pdf`);
    toast.success('Task analysis downloaded!');
  };

  // Helper to check if goal is ADL/functional
  const isADLGoal = () => {
    const domain = iepData?.iep_goal?.annual_goal?.area_of_need?.toLowerCase() || iepData?.scenario?.toLowerCase() || '';
    const adlKeywords = ['daily living', 'adl', 'self-care', 'hygiene', 'functional', 'life skill', 'adaptive', 'self-help', 'toileting', 'dressing', 'feeding', 'grooming'];
    return adlKeywords.some(keyword => domain.includes(keyword));
  };

  const downloadAsPdf = () => {
    if (!iepData?.iep_goal) return;
    
    const goal = iepData.iep_goal;
    const pdf = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;
    
    // Background
    pdf.setFillColor(253, 251, 247);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');
    
    // Header
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(20);
    pdf.setTextColor(93, 141, 120);
    pdf.text('IEP Goal Recommendation', pageWidth / 2, 25, { align: 'center' });
    
    pdf.setFontSize(12);
    pdf.setTextColor(86, 93, 94);
    pdf.text(`Student: ${iepData.student_name}`, pageWidth / 2, 35, { align: 'center' });
    pdf.text(`Grade: ${iepData.grade_level}`, pageWidth / 2, 42, { align: 'center' });
    
    // Area of Need Box
    let yPos = 55;
    pdf.setFillColor(93, 141, 120);
    pdf.roundedRect(margin, yPos, contentWidth, 20, 3, 3, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(255, 255, 255);
    pdf.text(`Area of Need: ${goal.annual_goal?.area_of_need || iepData.scenario}`, margin + 5, yPos + 8);
    pdf.text(`SEL Standard: ${goal.annual_goal?.sel_standard || iepData.sel_standard}`, margin + 5, yPos + 16);
    
    yPos += 30;
    
    // Annual Goal Section
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(45, 52, 54);
    pdf.text('Annual Goal', margin, yPos);
    yPos += 8;
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    const goalLines = pdf.splitTextToSize(goal.annual_goal?.goal_text || '', contentWidth);
    pdf.text(goalLines, margin, yPos);
    yPos += goalLines.length * 5 + 10;
    
    // Goal Details
    const details = [
      { label: 'Baseline', value: goal.annual_goal?.baseline },
      { label: 'Target Criteria', value: goal.annual_goal?.target_criteria },
      { label: 'Measurement Method', value: goal.annual_goal?.measurement_method },
      { label: 'Timeframe', value: goal.annual_goal?.timeframe }
    ];
    
    details.forEach(({ label, value }) => {
      if (value) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(93, 141, 120);
        pdf.text(`${label}:`, margin, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(45, 52, 54);
        const valueLines = pdf.splitTextToSize(value, contentWidth - 30);
        pdf.text(valueLines, margin + 35, yPos);
        yPos += valueLines.length * 4 + 6;
      }
    });
    
    // Objectives (if present)
    if (goal.objective_1) {
      yPos += 5;
      if (yPos > pageHeight - 80) {
        pdf.addPage();
        pdf.setFillColor(253, 251, 247);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        yPos = 25;
      }
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(244, 162, 97);
      pdf.text('Short-Term Objective 1', margin, yPos);
      yPos += 6;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(45, 52, 54);
      const obj1Lines = pdf.splitTextToSize(goal.objective_1?.goal_text || '', contentWidth);
      pdf.text(obj1Lines, margin, yPos);
      yPos += obj1Lines.length * 5 + 10;
    }
    
    if (goal.objective_2) {
      if (yPos > pageHeight - 60) {
        pdf.addPage();
        pdf.setFillColor(253, 251, 247);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        yPos = 25;
      }
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.setTextColor(244, 162, 97);
      pdf.text('Short-Term Objective 2', margin, yPos);
      yPos += 6;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(45, 52, 54);
      const obj2Lines = pdf.splitTextToSize(goal.objective_2?.goal_text || '', contentWidth);
      pdf.text(obj2Lines, margin, yPos);
      yPos += obj2Lines.length * 5 + 10;
    }
    
    // Progress Monitoring
    if (goal.progress_monitoring) {
      if (yPos > pageHeight - 40) {
        pdf.addPage();
        pdf.setFillColor(253, 251, 247);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        yPos = 25;
      }
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(45, 52, 54);
      pdf.text('Progress Monitoring:', margin, yPos);
      yPos += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      const pmLines = pdf.splitTextToSize(goal.progress_monitoring, contentWidth);
      pdf.text(pmLines, margin, yPos);
      yPos += pmLines.length * 5 + 8;
    }
    
    // Accommodations
    if (goal.accommodations_suggestions?.length > 0) {
      if (yPos > pageHeight - 40) {
        pdf.addPage();
        pdf.setFillColor(253, 251, 247);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        yPos = 25;
      }
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(45, 52, 54);
      pdf.text('Suggested Accommodations:', margin, yPos);
      yPos += 5;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      goal.accommodations_suggestions.forEach(acc => {
        pdf.text(`• ${acc}`, margin + 5, yPos);
        yPos += 5;
      });
    }
    
    // Footer
    pdf.setFontSize(8);
    pdf.setTextColor(138, 145, 146);
    pdf.text('Generated by SpEdKit - For IEP team consideration', pageWidth / 2, pageHeight - 10, { align: 'center' });
    
    pdf.save(`IEP_Goal_${iepData.student_name.replace(/\s+/g, '_')}.pdf`);
    toast.success('IEP goal PDF downloaded!');
  };

  const GoalSection = ({ title, goalData, color = 'primary' }) => {
    if (!goalData) return null;
    
    const colorClasses = {
      primary: 'border-primary bg-primary/5',
      accent: 'border-accent bg-accent/5'
    };
    
    return (
      <div className={`border-2 ${colorClasses[color]} rounded-xl p-4 mb-4`}>
        <div className="flex justify-between items-start mb-3">
          <h4 className="font-heading font-semibold text-text-primary">{title}</h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => copyToClipboard(goalData.goal_text, title)}
            className="text-text-muted hover:text-primary"
          >
            {copiedField === title ? (
              <Check className="w-4 h-4 text-primary" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </Button>
        </div>
        
        <p className="font-body text-text-primary mb-4 leading-relaxed">
          {goalData.goal_text}
        </p>
        
        <div className="grid grid-cols-2 gap-3 text-sm">
          {goalData.baseline && (
            <div>
              <span className="font-semibold text-primary">Baseline:</span>
              <p className="text-text-secondary">{goalData.baseline}</p>
            </div>
          )}
          {goalData.target_criteria && (
            <div>
              <span className="font-semibold text-primary">Target:</span>
              <p className="text-text-secondary">{goalData.target_criteria}</p>
            </div>
          )}
          {goalData.measurement_method && (
            <div>
              <span className="font-semibold text-primary">Measurement:</span>
              <p className="text-text-secondary">{goalData.measurement_method}</p>
            </div>
          )}
          {goalData.timeframe && (
            <div>
              <span className="font-semibold text-primary">Timeframe:</span>
              <p className="text-text-secondary">{goalData.timeframe}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            IEP Goal Generator
          </DialogTitle>
          <DialogDescription className="font-body">
            Generate a SMART IEP goal based on the social story for {studentName}
          </DialogDescription>
        </DialogHeader>

        {!iepData ? (
          <div className="py-6">
            {/* Options */}
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between p-4 bg-secondary/20 rounded-xl">
                <div>
                  <Label className="font-body font-semibold text-text-primary">
                    Include Short-Term Objectives
                  </Label>
                  <p className="text-sm text-text-muted mt-1">
                    Break the annual goal into two building objectives
                  </p>
                </div>
                <Switch
                  checked={includeObjectives}
                  onCheckedChange={setIncludeObjectives}
                  data-testid="include-objectives-switch"
                />
              </div>
              
              <div>
                <Label className="font-body font-semibold text-text-secondary mb-2 block">
                  Additional Context (optional)
                </Label>
                <Textarea
                  value={customContext}
                  onChange={(e) => setCustomContext(e.target.value)}
                  placeholder="Add any specific context about the student's current level, prior interventions, or specific focus areas..."
                  className="min-h-[80px]"
                  data-testid="custom-context-input"
                />
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-primary/10 rounded-xl p-4 mb-6">
              <h4 className="font-heading font-semibold text-primary mb-2">
                SMART Goal Format
              </h4>
              <ul className="text-sm text-text-secondary space-y-1">
                <li><span className="font-semibold">S</span>pecific - Clearly describes the skill</li>
                <li><span className="font-semibold">M</span>easurable - Includes criteria (%, frequency)</li>
                <li><span className="font-semibold">A</span>chievable - Appropriate for student level</li>
                <li><span className="font-semibold">R</span>elevant - Aligned to SEL standards</li>
                <li><span className="font-semibold">T</span>ime-bound - One school year timeframe</li>
              </ul>
            </div>

            <Button
              onClick={generateIEPGoal}
              disabled={loading}
              className="w-full bg-primary text-white hover:bg-primary/90 rounded-full py-3"
              data-testid="generate-iep-goal-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating SMART Goal...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate IEP Goal
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="py-4">
            {/* Summary Header */}
            <div className="bg-gradient-to-r from-primary to-primary-dark text-white rounded-xl p-4 mb-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-white/80 text-sm">Area of Need</p>
                  <p className="font-heading font-semibold">
                    {iepData.iep_goal?.annual_goal?.area_of_need || iepData.scenario}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-white/80 text-sm">SEL Standard</p>
                  <p className="font-heading font-semibold text-sm">
                    {iepData.sel_standard}
                  </p>
                </div>
              </div>
            </div>

            {/* Annual Goal */}
            <GoalSection 
              title="Annual Goal" 
              goalData={iepData.iep_goal?.annual_goal}
              color="primary"
            />

            {/* Objectives */}
            {(iepData.iep_goal?.objective_1 || iepData.iep_goal?.objective_2) && (
              <Collapsible open={objectivesExpanded} onOpenChange={setObjectivesExpanded}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full justify-between mb-2">
                    <span className="font-heading font-semibold text-accent">
                      Short-Term Objectives
                    </span>
                    {objectivesExpanded ? (
                      <ChevronUp className="w-5 h-5" />
                    ) : (
                      <ChevronDown className="w-5 h-5" />
                    )}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <GoalSection 
                    title="Objective 1 (Mid-Year)" 
                    goalData={iepData.iep_goal?.objective_1}
                    color="accent"
                  />
                  <GoalSection 
                    title="Objective 2 (Building to Mastery)" 
                    goalData={iepData.iep_goal?.objective_2}
                    color="accent"
                  />
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Progress Monitoring */}
            {iepData.iep_goal?.progress_monitoring && (
              <div className="bg-secondary/30 rounded-xl p-4 mb-4">
                <h4 className="font-heading font-semibold text-text-primary mb-2">
                  Progress Monitoring
                </h4>
                <p className="font-body text-text-secondary text-sm">
                  {iepData.iep_goal.progress_monitoring}
                </p>
              </div>
            )}

            {/* Accommodations */}
            {iepData.iep_goal?.accommodations_suggestions?.length > 0 && (
              <div className="bg-secondary/30 rounded-xl p-4 mb-4">
                <h4 className="font-heading font-semibold text-text-primary mb-2">
                  Suggested Accommodations
                </h4>
                <ul className="space-y-1">
                  {iepData.iep_goal.accommodations_suggestions.map((acc, i) => (
                    <li key={i} className="font-body text-text-secondary text-sm flex items-start gap-2">
                      <span className="text-primary">•</span>
                      {acc}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <Button
                onClick={downloadAsPdf}
                variant="outline"
                className="flex-1 border-2 border-primary text-primary hover:bg-primary/10 rounded-full"
                data-testid="download-iep-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
              <Button
                onClick={() => {
                  setIepData(null);
                  setCustomContext('');
                  setWorksheetData(null);
                  setPblData(null);
                  setTaskAnalysisData(null);
                }}
                variant="outline"
                className="flex-1 border-2 border-accent text-accent hover:bg-accent/10 rounded-full"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generate New
              </Button>
            </div>

            {/* Additional Resources - Worksheets & PBL */}
            <div className="mt-6 pt-6 border-t border-secondary/50">
              <h4 className="font-heading font-semibold text-text-primary mb-3">
                Additional Resources
              </h4>
              
              <div className="grid gap-3">
                {/* Progress Monitoring Worksheet */}
                <div className="bg-blue-50 rounded-xl p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <ClipboardList className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-blue-800">Progress Monitoring Worksheet</p>
                        <p className="text-xs text-blue-600">Downloadable data collection sheet</p>
                      </div>
                    </div>
                    {!worksheetData ? (
                      <Button
                        onClick={generateWorksheet}
                        disabled={generatingWorksheet}
                        size="sm"
                        className="bg-blue-600 text-white hover:bg-blue-700"
                        data-testid="generate-worksheet-btn"
                      >
                        {generatingWorksheet ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Generate'
                        )}
                      </Button>
                    ) : (
                      <Button
                        onClick={downloadWorksheetPdf}
                        size="sm"
                        variant="outline"
                        className="border-blue-600 text-blue-600 hover:bg-blue-50"
                        data-testid="download-worksheet-btn"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    )}
                  </div>
                  {worksheetData && (
                    <p className="text-xs text-blue-600 mt-2 italic">
                      ✓ Worksheet ready: {worksheetData.worksheet?.worksheet_title || 'Progress Monitoring'}
                    </p>
                  )}
                </div>

                {/* Project-Based Learning Ideas */}
                <div className="bg-amber-50 rounded-xl p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-amber-600" />
                      <div>
                        <p className="font-medium text-amber-800">Project-Based Learning Ideas</p>
                        <p className="text-xs text-amber-600">Hands-on projects aligned to this goal</p>
                      </div>
                    </div>
                    {!pblData ? (
                      <Button
                        onClick={generatePBL}
                        disabled={generatingPBL}
                        size="sm"
                        className="bg-amber-600 text-white hover:bg-amber-700"
                        data-testid="generate-pbl-btn"
                      >
                        {generatingPBL ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Generate'
                        )}
                      </Button>
                    ) : (
                      <Button
                        onClick={downloadPBLPdf}
                        size="sm"
                        variant="outline"
                        className="border-amber-600 text-amber-600 hover:bg-amber-50"
                        data-testid="download-pbl-btn"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    )}
                  </div>
                  {pblData?.pbl_ideas?.projects && (
                    <p className="text-xs text-amber-600 mt-2 italic">
                      ✓ {pblData.pbl_ideas.projects.length} project ideas ready
                    </p>
                  )}
                </div>

                {/* Task Analysis Worksheet - for ADL/functional goals */}
                <div className="bg-emerald-50 rounded-xl p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <ListChecks className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="font-medium text-emerald-800">Task Analysis Worksheet</p>
                        <p className="text-xs text-emerald-600">Step-by-step breakdown for ADL/functional skills {isADLGoal() ? '(Recommended)' : ''}</p>
                      </div>
                    </div>
                    {!taskAnalysisData ? (
                      <Button
                        onClick={generateTaskAnalysis}
                        disabled={generatingTaskAnalysis}
                        size="sm"
                        className="bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid="generate-task-analysis-btn"
                      >
                        {generatingTaskAnalysis ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Generate'
                        )}
                      </Button>
                    ) : (
                      <Button
                        onClick={downloadTaskAnalysisPdf}
                        size="sm"
                        variant="outline"
                        className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                        data-testid="download-task-analysis-btn"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    )}
                  </div>
                  {taskAnalysisData && (
                    <p className="text-xs text-emerald-600 mt-2 italic">
                      ✓ Ready: {taskAnalysisData.task_analysis?.task_title || 'Task Analysis'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <p className="text-xs text-text-muted text-center mt-4">
              This goal is a recommendation for IEP team consideration. 
              Always review and customize based on the full IEP process.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-full">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default IEPGoalGenerator;
